import type { SectionTitle } from './OnboardingPage';

/**
 * What an editing step says about when its changes take effect (Rob, 2026-10-01), in the
 * Onboarding Glass Panels canvas's wording (`onboarding-persistent-scene-step-titles`, R2): the
 * step's status line in the nav bar where it has no caption, and the Observers notices panel's line.
 *
 * Keyed on the step's TITLE as `STEPS` in `OnboardingPage.tsx` spells it, not on its id: the
 * step list lives inside the page module, which cannot be imported into a plain test without
 * stubbing the planner. The titles are typed as `SectionTitle` through a TYPE-ONLY import (erased at
 * build, so nothing of the page loads here), so renaming a title fails the type check rather than
 * silently dropping that step's note.
 *
 * Twin details, Opening hours and Position twin have no note because they save to the twin at
 * once; Launch has none because it IS the final step. Equipment/Assets carries the line although its
 * register writes land at once — on a live twin whose entity has no capacity, a register capacity
 * or match edit can reach the Listener before Launch. Rob accepted that exception and kept the line
 * (2026-10-01).
 */
const LAUNCH_NOTE_STEPS: ReadonlySet<SectionTitle> = new Set<SectionTitle>([
  'Equipment/Assets',
  'Floor Plan',
  'Camera Feeds',
  'Observers',
  'Composite measures',
]);

/** The canvas's one line for every editing step; no closing full stop, as the canvas writes it. */
const LAUNCH_NOTE = 'Changes apply when the twin is launched (final step)';

/** The note for the step titled `title`, or `null` for a step that carries none. */
export const launchNote = (title: string | null): string | null =>
  title !== null && (LAUNCH_NOTE_STEPS as ReadonlySet<string>).has(title) ? LAUNCH_NOTE : null;
